import { Box3, Vector3 } from 'three'
import { ConvexGeometry } from 'three/addons/geometries/ConvexGeometry.js'

export type VehicleConvexHull = {
  /** Convex-shell vertices in VEHICLE_ROOT local space, including collision skin. */
  vertices: Vector3[]
  bounds: Box3
  sourcePointCount: number
  faceCount: number
  skinM: number
}

export type VehicleConvexHullOptions = {
  voxelSizeM?: number
  skinM?: number
}

function quantisedKey(point: Vector3, quantum: number) {
  return `${Math.round(point.x / quantum)}|${Math.round(point.y / quantum)}|${Math.round(point.z / quantum)}`
}

function uniqueFinitePoints(points: readonly Vector3[], quantum: number) {
  const unique = new Map<string, Vector3>()
  for (const point of points) {
    if (![point.x, point.y, point.z].every(Number.isFinite)) continue
    const key = quantisedKey(point, quantum)
    if (!unique.has(key)) unique.set(key, point.clone())
  }
  return [...unique.values()]
}

/**
 * Builds a reusable convex chassis collider from an authored render-mesh cloud.
 *
 * Import-time voxel welding removes duplicate split normals/material seams, then
 * Three's QuickHull implementation extracts only the exterior shell. The final
 * shell is expanded by a tiny skin so the toon outline does not visibly enter a
 * road before contact is resolved. This runs once per vehicle asset, never per
 * physics step.
 */
export function buildVehicleConvexHull(
  sourcePoints: readonly Vector3[],
  options: VehicleConvexHullOptions = {},
): VehicleConvexHull {
  const voxelSizeM = Math.max(0.001, options.voxelSizeM ?? 0.008)
  const skinM = Math.max(0, options.skinM ?? 0.02)
  const welded = uniqueFinitePoints(sourcePoints, voxelSizeM)
  if (welded.length < 4) throw new Error(`Vehicle collider needs at least 4 unique points; received ${welded.length}.`)

  const geometry = new ConvexGeometry(welded)
  const positions = geometry.getAttribute('position')
  if (!positions || positions.count < 12) {
    geometry.dispose()
    throw new Error('Vehicle collider point cloud is degenerate; QuickHull could not make a volume.')
  }

  const rawHullVertices: Vector3[] = []
  for (let index = 0; index < positions.count; index += 1) {
    rawHullVertices.push(new Vector3(positions.getX(index), positions.getY(index), positions.getZ(index)))
  }
  const vertices = uniqueFinitePoints(rawHullVertices, 0.0001)
  const bounds = new Box3().setFromPoints(vertices)
  const centre = bounds.getCenter(new Vector3())
  if (skinM > 0) {
    for (const vertex of vertices) {
      const direction = vertex.clone().sub(centre)
      if (direction.lengthSq() > 1e-10) vertex.addScaledVector(direction.normalize(), skinM)
    }
    bounds.setFromPoints(vertices)
  }
  const faceCount = positions.count / 3
  geometry.dispose()

  return { vertices, bounds, sourcePointCount: welded.length, faceCount, skinM }
}

/** Keeps terrain probing bounded even when a future authored car is unusually dense. */
export function lowestVehicleHullVertices(
  hull: VehicleConvexHull,
  rotation: { x: number, y: number, z: number, w: number },
  maximum = 20,
  verticalBandM = 0.55,
) {
  const ranked = hull.vertices.map((local) => ({
    local,
    rotated: local.clone().applyQuaternion(rotation),
  })).sort((a, b) => a.rotated.y - b.rotated.y)
  const cutoff = ranked[0].rotated.y + verticalBandM
  return ranked.filter((entry) => entry.rotated.y <= cutoff).slice(0, maximum)
}
